import React from 'react';
import { ToggleButton, ToggleButtonGroup, Stack, Typography } from '@mui/material';
import CheckIcon from '@mui/icons-material/Check';
import { TOUCH_TARGET_COARSE } from '../../theme/tokens';

/**
 * RangeToggle — the frame segment: a period or a unit for KEY FIGURES.
 *
 * @description
 *   Two to five mutually exclusive values, one of them always chosen. It sits
 *   next to charts and KPI rows and changes HOW a figure is read — it never
 *   hides records (concept §3.2, N3.5: a period that hides records is a date
 *   filter, not a segment). The visible caption stands to its left; the group
 *   carries the same word as its accessible name, because a sibling `Typography`
 *   is not a name (R16 finding, 2026-10-09).
 *
 *   `options` is caller data: plain values (`'30'` → „30d"), `'all'` → „All",
 *   `'lifetime'` → „Lifetime", or `{ value, label }` for anything else. 🔴 The
 *   ORDER is the caller's too — N3.5 puts „All"/„Lifetime" LAST, and every
 *   caller already does; the component does not re-sort, because re-sorting
 *   would silently change the `options` contract of three existing callers.
 *
 * @useWhen
 *   - A period or unit above a chart, a KPI row or a history table
 *   - 2–5 mutually exclusive values where one is always chosen
 *
 * @dontUseFor
 *   - A filter that HIDES records — that is a date filter (`DateRangeFilter`) or a facet
 *   - More than five values — that is a `BottomSheetSelect`
 *   - A quick filter that may be off — that is `FilterPillRow`
 *
 * @related
 *   - FilterPillRow — the other row family: several conditions at once, each may be off
 *   - DateRangeFilter — a date range as a FILTER (hides records), not as a frame
 *   - BottomSheetSelect — a facet with more than five values
 *
 * @reuseHints
 *   - A unit switch on a report (kg / pieces), a comparison window on a table
 *
 * Props:
 *   value       : string                — the chosen value
 *   onChange    : (value) => void       — called with the next value; `null` is ignored
 *   options     : array                 — plain values or `{ value, label }`
 *   label       : string                — the visible caption left of the group AND the
 *                                         group's accessible name (default „Range")
 *   checkActive : bool                  — 🔴 NEW (UI2 Teil 10 G0.6, BS9), default `false`
 *                                         = the old form. `true` puts a check mark in EVERY
 *                                         segment and reveals it only on the CHOSEN one
 *                                         (the others carry it `visibility: 'hidden'`, so the
 *                                         mark keeps its place and the row does not jump when
 *                                         the value changes): „chosen" then carries a shape,
 *                                         not only a fill (N3.4, never colour alone).
 *                                         Without the prop nothing changes — no icon is
 *                                         rendered at all.
 *   touchFloor  : bool                  — 🔴 NEW (UI2 Teil 10 G13b-2, 2026-10-28), default
 *                                         `false` = the old form. `true` puts the 44-px floor
 *                                         (`TOUCH_TARGET_COARSE`) on the SEGMENTS. Without the
 *                                         prop no media rule is emitted for the segments.
 *                                         🔴 It is the FLOOR, not a height: a caller that
 *                                         already passes `FIELD_HEIGHT_SX` (which carries the
 *                                         same floor) gets no extra pixel from it.
 *                                         🔴 **Corrected in round 1 of the gates
 *                                         (2026-10-29): the floor is NOT the prop's doing.**
 *                                         `theme.js` (`MuiToggleButton.root`, `:844-868`) spreads
 *                                         `TOUCH_TARGET_COARSE_ACTIVE` — `minHeight: 44` under
 *                                         `@media (pointer: coarse)` — onto EVERY toggle button
 *                                         in the app. It takes that floor back only in the dense
 *                                         contexts it names (`MuiTableCell-root`,
 *                                         `MuiTablePagination-root`, `MuiToolbar-dense`, from
 *                                         `sm` up); there, and only there, the prop is the way
 *                                         to get it. On a plain caller it is a DELIBERATE
 *                                         DUPLICATE of the theme (third copy after
 *                                         `FIELD_HEIGHT_SX`).
 *                                         🔴 **What it does not do, measured:** removing it does
 *                                         not make a segment short. The pixel probes split it
 *                                         exactly that way — at 1440 the height token carries the
 *                                         row (P4 goes red when the `sx` height goes, not when the
 *                                         prop goes), and at 390 the theme carries the floor (P14
 *                                         stays green without the prop). The prop's own proof is
 *                                         the emitted rule, `RangeToggle.touchFloor.test.jsx`.
 *   disabled    : bool                  — 🔴 NEW (G13b-2, 2026-10-28), default `false` = the
 *                                         old form. `true` disables every segment (the N8.5
 *                                         first-build lock: the controls stay visible and stop
 *                                         taking input). Without the prop nothing changes.
 *   sx          : object                — extra sx on the row
 */
export default function RangeToggle({
  value,
  onChange,
  options = ['7', '30', '90', 'all'],
  label = 'Range',
  checkActive = false,
  touchFloor = false,
  disabled = false,
  sx,
}) {
  const normalized = options.map((opt) => {
    if (typeof opt === 'object') return opt;
    const val = typeof opt === 'number' ? String(opt) : opt;
    let text = val === 'all' ? 'All' : `${val}d`;
    // `lifetime` ist ein Wort, keine Tageszahl: ohne diesen Zweig stuende dort
    // „lifetimed". Additiv — kein Aufrufer uebergibt es heute (grep 0).
    if (val === 'lifetime') text = 'Lifetime';
    return { value: val, label: text };
  });

  const handleChange = (_, next) => {
    if (next === null) return;
    onChange?.(next);
  };

  return (
    <Stack direction="row" spacing={1} alignItems="center" sx={sx}>
      {label && (
        <Typography variant="caption" color="text.secondary">
          {label}
        </Typography>
      )}
      <ToggleButtonGroup
        value={value}
        exclusive
        onChange={handleChange}
        size="small"
        disabled={disabled}
        // 🔴 Der sichtbare Text („Range") ist eine eigene Typography daneben und
        // damit KEIN Name der Gruppe — ein Screenreader las vier Schalter ohne
        // gemeinsame Beschriftung. `label` traegt den Namen; ein leeres `label`
        // faellt auf „Range" zurueck, die Gruppe bleibt also benannt.
        aria-label={label || 'Range'}
        sx={{
          '& .MuiToggleButton-root': {
            fontWeight: 600,
            fontSize: 12,
            borderRadius: 999,
            px: 1.5,
            // 🔴 G13b-2: der 44-px-Boden auf grobem Zeiger an DIESER Aufrufstelle.
            // Ohne die Prop steht in diesem `sx` nichts, und die hier emittierte
            // Regel ist zeichengleich zur alten Fassung. 🔴 Der Boden selbst ist
            // keine Zutat der Prop: `theme.js` (`MuiToggleButton.root`) setzt ihn
            // für jedes Segment ohnehin (s. `touchFloor` im Kopf dieser Datei).
            ...(touchFloor ? TOUCH_TARGET_COARSE : {}),
          },
        }}
      >
        {normalized.map((opt) => (
          <ToggleButton key={opt.value} value={opt.value}>
            {/* Nur MIT `checkActive` — dann aber in JEDEM Segment: das Symbol ist
                in den nicht gewaehlten `visibility: 'hidden'` und haelt so seinen
                Platz. Ohne die Reservierung waechst nur das gewaehlte Segment,
                und die Reihe springt beim Umschalten (R16 B2; dieselbe Regel wie
                die Zaehler in T.4). `fontSize="small"` ist die Hausform
                (BottomSheetSelect.jsx). MUI zeichnet das Symbol `aria-hidden`,
                der Name des Knopfes bleibt sein Text. */}
            {checkActive && (
              <CheckIcon
                fontSize="small"
                sx={{ visibility: opt.value === value ? 'visible' : 'hidden' }}
              />
            )}
            {opt.label}
          </ToggleButton>
        ))}
      </ToggleButtonGroup>
    </Stack>
  );
}
